import { useCallback, useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { useGraphStore } from '@/store/graphStore';
import { parseEnrichmentToElements } from '@/utils/enrichmentParser';
import type { InfrastructureGraphHandle } from '@/components/InfrastructureGraph';
import type { GodEnrichmentResult, ScanJobResponse } from '@/types/dns';
import { useWebSocket } from './useWebSocket';

/**
 * The public scan API returns a nested result shape that differs from the flat
 * GodEnrichmentResult the parser expects. This function normalises it.
 *
 * Actual shape:
 *   result.dns.dns.{a_records, ns_records, mx_records, ...}
 *   result.risk_score.risk_score.score
 *   result.ip_intel.{reputation_score, country, ...}
 *   result.threat.{sources, is_malicious, threat_level}
 */
function normalizeApiResult(raw: GodEnrichmentResult): GodEnrichmentResult {
  const r = raw as Record<string, unknown>;
  const dns    = ((r.dns as Record<string, unknown>)?.dns  ?? {}) as Record<string, unknown>;
  const rs     = ((r.risk_score as Record<string, unknown>)?.risk_score ?? {}) as Record<string, unknown>;
  const intel  = (r.ip_intel ?? {}) as Record<string, unknown>;
  const threat = (r.threat   ?? {}) as Record<string, unknown>;

  return {
    ...raw,
    a_records:     (dns.a_records     as string[] | undefined) ?? raw.a_records,
    aaaa_records:  (dns.aaaa_records  as string[] | undefined) ?? raw.aaaa_records,
    ns_records:    (dns.ns_records    as string[] | undefined) ?? raw.ns_records,
    mx_records:    (dns.mx_records    as GodEnrichmentResult['mx_records']) ?? raw.mx_records,
    cname_records: (dns.cname_records as string[] | undefined) ?? raw.cname_records,
    threat_score:  (rs.score          as number   | undefined) ?? raw.threat_score,
    country:       (intel.country     as string   | undefined) ?? raw.country,
    abuse_score:   (intel.reputation_score as number | undefined) ?? raw.abuse_score,
    feeds:         (threat.sources    as string[] | undefined) ?? raw.feeds,
  };
}

const POLL_INTERVAL_MS = 2000;

interface UseScanStreamReturn {
  startScan: (target: string) => Promise<void>;
  scanning: boolean;
  error: string | null;
}

export function useScanStream(
  graphRef: RefObject<InfrastructureGraphHandle | null>,
): UseScanStreamReturn {
  const addElements = useGraphStore((state) => state.addElements);
  const setScanning = useGraphStore((state) => state.setScanning);
  const setScanError = useGraphStore((state) => state.setScanError);
  const setCurrentJobId = useGraphStore((state) => state.setCurrentJobId);
  const setScanProgress = useGraphStore((state) => state.setScanProgress);
  const clearGraph = useGraphStore((state) => state.clearGraph);
  const setLastScanResult = useGraphStore((state) => state.setLastScanResult);
  const scanning = useGraphStore((state) => state.scanning);
  const scanError = useGraphStore((state) => state.scanError);
  const currentJobId = useGraphStore((state) => state.currentJobId);
  const pollTimerRef = useRef<number | null>(null);

  // WebSocket: opens when currentJobId is set, closes when cleared
  useWebSocket({
    jobId: currentJobId,
    graphRef,
    onError: (msg) => setScanError(msg),
  });

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // Cancel polling interval when the hook unmounts (e.g. navigating away mid-scan)
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const startScan = useCallback(
    async (target: string) => {
      if (scanning) return;

      // Reset state for new scan
      stopPolling();
      clearGraph();
      setScanning(true);
      setScanError(null);
      setCurrentJobId(null);
      setScanProgress(null);

      // --- Step 1: Submit scan ---
      let jobId: string;
      try {
        const resp = await fetch('/api/scan/public', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ target: target.trim().toLowerCase() }),
        });

        if (!resp.ok) {
          const errBody = await resp.json().catch(() => ({})) as { detail?: string };
          throw new Error(errBody.detail ?? `HTTP ${resp.status}`);
        }

        const data = (await resp.json()) as ScanJobResponse;
        jobId = data.job_id;
        setCurrentJobId(jobId);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Scan submission failed';
        setScanError(msg);
        setScanning(false);
        return;
      }

      // --- Step 2: Poll until completed or failed ---
      pollTimerRef.current = window.setInterval(async () => {
        try {
          const resp = await fetch(`/api/scan/public/${jobId}`);
          if (!resp.ok) {
            stopPolling();
            setScanError(`Poll error: HTTP ${resp.status}`);
            setScanning(false);
            setCurrentJobId(null);
            return;
          }

          const job = (await resp.json()) as ScanJobResponse;

          if (job.status === 'completed') {
            stopPolling();
            setCurrentJobId(null); // Closes WebSocket
            setScanProgress(null);

            if (job.result) {
              const normalized = normalizeApiResult(job.result);
              const elements = parseEnrichmentToElements(normalized, job.target);
              addElements(elements);
              // Also push to the live cy instance (handles the case where WS missed nodes)
              graphRef.current?.batchAdd(elements);
              setLastScanResult(job.result, job.target, job.job_id);
            }

            setScanning(false);
          } else if (job.status === 'failed') {
            stopPolling();
            setCurrentJobId(null);
            setScanProgress(null);
            setScanError(job.error ?? 'Scan failed');
            setScanning(false);
          } else if (job.progress != null) {
            // status === 'running' with progress — update bar
            setScanProgress(job.progress);
          }
        } catch (err) {
          stopPolling();
          const msg = err instanceof Error ? err.message : 'Poll error';
          setScanError(msg);
          setScanning(false);
          setCurrentJobId(null);
        }
      }, POLL_INTERVAL_MS);
    },
    [
      scanning,
      stopPolling,
      clearGraph,
      setScanning,
      setScanError,
      setCurrentJobId,
      setScanProgress,
      addElements,
      setLastScanResult,
      graphRef,
    ],
  );

  return { startScan, scanning, error: scanError };
}
